import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { isPast } from "date-fns";

export function MahasarathiAlerts() {
  const { tenant } = useAuth();
  const navigate = useNavigate();

  const { data: alerts, isLoading } = useQuery({
    queryKey: ["mahasarathi_alerts", tenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mahasarathi_applications")
        .select("*")
        .eq("tenant_id", tenant!.id)
        .in("status", ['Processing', 'OTP Required', 'Card Ready', 'Timer Expired'])
        .order("timer_deadline", { ascending: true });
        
      if (error) throw error;
      
      return (data as any[]).filter(app => {
        if (app.status === 'Processing') {
          return isPast(new Date(app.timer_deadline)); // Show if timer is up
        }
        return true; // Show all OTP Required and Card Ready
      });
    },
    enabled: !!tenant?.id,
  });

  if (isLoading || !alerts || alerts.length === 0) {
    return null; // Don't show the card if there are no alerts
  }

  return (
    <Card className="border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 shadow-sm animate-in fade-in slide-in-from-bottom-2">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-red-700 dark:text-red-400 flex items-center gap-2 text-base font-semibold">
          <ShieldAlert className="h-5 w-5 animate-pulse" />
          Mahasarathi Action Required ({alerts.length})
        </CardTitle>
        <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800 hover:bg-red-100 dark:text-red-400 h-8" onClick={() => navigate('/mahasarathi')}>
          View All <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-2">
          {alerts.slice(0, 3).map(app => (
            <div key={app.id} className="flex justify-between items-center text-sm bg-white dark:bg-black/20 p-2 rounded border border-red-100 dark:border-red-900/50">
              <span className="font-medium text-red-900 dark:text-red-300">{app.customer_name}</span>
              <span className="text-red-600 dark:text-red-400 font-semibold text-xs">
                {app.status === 'Processing' ? 'Timer Expired' : app.status}
              </span>
            </div>
          ))}
          {alerts.length > 3 && (
            <div className="text-xs text-center text-red-600/70 pt-1">
              + {alerts.length - 3} more applications need your attention
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
